import { useEffect, useState } from 'react';
// (BillingPanel + UsageCard defined below)
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar.jsx';
import api from '../lib/api.js';
import { getUser } from '../lib/auth.jsx';

export default function MemberDashboard() {
  const u = getUser();
  const [jobs, setJobs] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState({}); // jobId -> bool

  const reload = async () => {
    setLoading(true);
    try {
      const [j, us] = await Promise.all([api.listJobs({ mine: '1' }), api.getUsage()]);
      setJobs(j.jobs || []);
      setUsage(us);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="tag tag-gold mb-2">Member</div>
            <h1 className="text-3xl font-bold text-navy">Welcome back, {u?.name?.split(' ')[0] || 'there'}</h1>
            <p className="text-ink-muted text-sm mt-1">Your map jobs, usage, downloads, and quick actions.</p>
          </div>
          <Link to="/jobs/new" className="btn btn-gold">+ New Job</Link>
        </div>

        {err && <div className="card card-pad mb-6 border-red-200 bg-red-50 text-red-700 text-sm">{err}</div>}

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <UsageCard label="Maps this month"  used={usage?.maps_used ?? 0}    cap={usage?.maps_cap ?? 0} />
          <UsageCard label="Export bundles"   used={usage?.exports_used ?? 0} cap={usage?.exports_cap ?? 0} />
          <UsageCard label="DOCX reports"     used={usage?.reports_used ?? 0} cap={usage?.reports_cap ?? 0} />
        </div>

        <BillingPanel currentTier={usage?.tier} />


        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-navy">Recent jobs</h2>
            <div className="text-xs text-ink-muted">
              {jobs.length} job{jobs.length === 1 ? '' : 's'}
              {usage?.redownloads_per_file !== undefined && (
                <span className="ml-3">
                  Redownload limit per file: <span className="font-mono text-navy">{usage.redownloads_per_file === -1 ? '∞' : usage.redownloads_per_file}</span>/mo
                </span>
              )}
            </div>
          </div>

          {loading && <div className="p-6 text-sm text-ink-muted">Loading…</div>}

          {!loading && jobs.length === 0 && (
            <div className="p-10 text-center">
              <div className="text-gold text-5xl mb-3">📍</div>
              <div className="font-semibold text-navy mb-1">No jobs yet</div>
              <div className="text-sm text-ink-muted mb-4">Onboard a client and run your first map.</div>
              <Link to="/jobs/new" className="btn btn-gold">Create your first job</Link>
            </div>
          )}

          {!loading && jobs.length > 0 && (
            <div className="divide-y divide-slate-100">
              {jobs.map(j => {
                const isOpen = expanded[j.id];
                const files = j.delivery?.files || [];
                const counts = j.download_counts || {};
                return (
                  <div key={j.id}>
                    <button onClick={() => toggle(j.id)} className="w-full text-left px-6 py-4 hover:bg-slate-50 flex items-center gap-4">
                      <div className="text-ink-muted text-sm">{isOpen ? '▾' : '▸'}</div>
                      <div className="flex-1 grid md:grid-cols-5 gap-4 items-center text-sm">
                        <div>
                          <div className="font-semibold text-navy">{j.client_name || '—'}</div>
                          <div className="font-mono text-[10px] text-ink-muted">{j.id.slice(0, 8)}</div>
                        </div>
                        <div><JobStatusTag s={j.status} /></div>
                        <div className="text-xs text-ink-muted">{(j.formats || []).join(', ') || '—'}</div>
                        <div className="text-xs text-ink-muted">{files.length} file{files.length === 1 ? '' : 's'}</div>
                        <div className="text-xs text-ink-muted">{fmt(j.created_at)}</div>
                      </div>
                    </button>
                    {isOpen && (
                      <JobDownloads
                        job={j}
                        files={files}
                        counts={counts}
                        cap={usage?.redownloads_per_file}
                        onRefresh={reload}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function JobDownloads({ job, files, counts, cap, onRefresh }) {
  const [busy, setBusy] = useState({});  // filename -> bool
  const [err, setErr] = useState('');

  const onDownload = async (filename) => {
    setErr(''); setBusy(p => ({ ...p, [filename]: true }));
    try {
      const r = await api.downloadFile(job.id, filename);
      if (r.url) {
        window.open(r.url, '_blank', 'noopener');
      } else {
        // No real blob URL yet (MVP). Show toast-style hint inline.
        setErr(`Counter incremented. File available in your client workspace (${r.downloads_used}/${r.downloads_cap === -1 ? '∞' : r.downloads_cap}).`);
      }
      onRefresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(p => ({ ...p, [filename]: false }));
    }
  };

  if (files.length === 0) {
    return <div className="px-6 pb-6 -mt-1 text-sm text-ink-muted bg-slate-50">No deliverables yet. Job status: {job.status}.</div>;
  }

  return (
    <div className="px-6 pb-6 -mt-1 bg-slate-50">
      <div className="grid md:grid-cols-2 gap-3">
        {files.map(f => {
          const used = Number(counts[f.name] || 0);
          const unlimited = cap === -1 || cap === undefined;
          const remaining = unlimited ? -1 : Math.max(0, cap - used);
          const capped = !unlimited && remaining <= 0;
          return (
            <div key={f.name} className="bg-white rounded-lg p-4 border border-slate-200 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-ink-muted">{guessFormat(f.name)}</div>
                <div className="font-mono text-xs text-navy truncate">{f.name}</div>
                <div className="mt-1 text-[11px] text-ink-muted">
                  Downloads used: <span className="font-mono text-navy">{used}</span>
                  {' / '}<span className="font-mono">{unlimited ? '∞' : cap}</span>
                  {!unlimited && remaining > 0 && remaining <= 2 && (
                    <span className="ml-2 text-amber-700 font-semibold">{remaining} left</span>
                  )}
                </div>
              </div>
              <button
                className="btn btn-sm btn-primary whitespace-nowrap"
                disabled={busy[f.name] || capped}
                onClick={() => onDownload(f.name)}
                title={capped ? 'Monthly redownload limit reached. Ask your admin for more.' : 'Download / redownload this file'}
              >
                {capped ? 'Limit reached' : (busy[f.name] ? '…' : '↓ Download')}
              </button>
            </div>
          );
        })}
      </div>
      {job.delivery?.folder_url && (
        <div className="mt-3">
          <a className="btn btn-sm btn-ghost" target="_blank" rel="noreferrer" href={job.delivery.folder_url}>Open client workspace ↗</a>
        </div>
      )}
      {err && <div className="mt-3 text-xs text-red-600">{err}</div>}
    </div>
  );
}

function UsageCard({ label, used, cap }) {
  const unlimited = cap === -1 || cap === undefined || cap === null;
  const pct = !unlimited && cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const colorClass = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  const remaining = unlimited ? -1 : Math.max(0, cap - used);
  return (
    <div className="card card-pad">
      <div className="text-xs uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-3xl font-bold text-navy">{used}</span>
        <span className="text-ink-muted">/ {unlimited ? '∞' : cap}</span>
      </div>
      {!unlimited && cap > 0 && (
        <>
          <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${colorClass}`} style={{ width: pct + '%' }} />
          </div>
          <div className="mt-2 text-xs text-ink-muted">{remaining} left this month</div>
        </>
      )}
      {unlimited && <div className="mt-3 text-xs text-gold-600 uppercase tracking-widest font-semibold">Unlimited</div>}
    </div>
  );
}

function JobStatusTag({ s }) {
  if (s === 'completed') return <span className="tag tag-green">✓ Completed</span>;
  if (s === 'running')   return <span className="tag tag-amber">⏳ Running</span>;
  if (s === 'failed')    return <span className="tag tag-red">⚠ Failed</span>;
  if (s === 'draft')     return <span className="tag tag-navy">Draft</span>;
  if (s === 'profiled')  return <span className="tag tag-navy">Profiled</span>;
  if (s === 'mapped')    return <span className="tag tag-navy">Mapped</span>;
  return <span className="tag tag-navy">{s}</span>;
}

function guessFormat(name) {
  const n = name.toLowerCase();
  if (n.endsWith('.docx')) return 'DOCX';
  if (n.endsWith('.pptx')) return 'PPTX';
  if (n.endsWith('.geojson')) return 'GeoJSON';
  if (n.endsWith('.kml')) return 'KML';
  if (n.endsWith('.kmz')) return 'KMZ';
  if (n.endsWith('.csv')) return 'CSV';
  if (n.endsWith('.fgdb.zip')) return 'File GDB';
  if (n.endsWith('.zip')) return 'Shapefile';
  return 'FILE';
}

function fmt(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
}

function BillingPanel({ currentTier }) {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    api.billingStatus().then(setB).catch(e => setErr(e.message));
  }, []);
  if (!b) return null;

  const upgrade = async (tier) => {
    setBusy(tier); setErr('');
    try {
      const r = await api.billingCheckout(tier);
      if (r.url) window.location.href = r.url;
    } catch (e) { setErr(e.message); }
    finally { setBusy(''); }
  };
  const portal = async () => {
    setBusy('portal'); setErr('');
    try { const r = await api.billingPortal(); if (r.url) window.location.href = r.url; }
    catch (e) { setErr(e.message); }
    finally { setBusy(''); }
  };

  if (!b.enabled) {
    return (
      <div className="card card-pad mb-8 bg-slate-50">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted">Plan</div>
            <div className="text-lg font-semibold text-navy capitalize">{currentTier || b.tier || 'starter'}</div>
            <div className="text-sm text-ink-muted mt-1">Self-service billing is not yet enabled. To upgrade or change tier, email <a className="text-navy font-semibold" href="mailto:info@atlasgeo.ai">info@atlasgeo.ai</a>.</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="card card-pad mb-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-ink-muted">Current plan</div>
          <div className="text-lg font-semibold text-navy capitalize">{currentTier || b.tier || 'starter'}</div>
        </div>
        {b.stripe_customer_id && (
          <button className="btn btn-ghost" disabled={busy === 'portal'} onClick={portal}>{busy === 'portal' ? 'Opening…' : 'Manage billing'}</button>
        )}
      </div>
      {err && <div className="text-sm text-red-700 mb-2">{err}</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {['solo', 'starter', 'professional', 'enterprise'].map(t => {
          const isCurrent = (currentTier || b.tier) === t;
          const meta = b.tiers?.[t];
          return (
            <button key={t} disabled={busy === t || isCurrent} onClick={() => upgrade(t)}
              className={`text-left p-4 rounded-lg border-2 transition ${isCurrent ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-gold hover:shadow-soft'}`}>
              <div className="text-xs uppercase tracking-widest text-ink-muted">{meta?.label || t}</div>
              <div className="text-xl font-bold text-navy">${meta?.price || '—'}/mo</div>
              <div className="mt-1 text-xs text-ink-muted">
                {isCurrent ? 'Current plan' : (busy === t ? 'Opening Stripe…' : 'Switch to this plan')}
              </div>
            </button>
          );
        })}
      </div>

      <AddonsPanel />
    </div>
  );
}

// Per-service add-on menu. Universal $30–$50 menu available to every tier.
// User picks an add-on + a target job, click Buy → opens Stripe Checkout
// (one-time payment), webhook records `addons_paid` on the job, user retries
// the deliverable.
function AddonsPanel() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const ADDONS = [
    { key: 'docx_report',  label: 'DOCX report',                price: 50 },
    { key: 'shapefile',    label: 'Shapefile export',           price: 40 },
    { key: 'fgdb',         label: 'File Geodatabase (FGDB)',    price: 50 },
    { key: 'agol_publish', label: 'AGOL self-publish',          price: 50 },
    { key: 'tak_kmz',      label: 'ATAK / iTAK KMZ overlay',    price: 40 },
    { key: 'csv_export',   label: 'CSV export',                 price: 30 },
    { key: 'kml',          label: 'KML export',                 price: 30 },
    { key: 'geojson',      label: 'GeoJSON export',             price: 30 },
    { key: 'single_map',   label: 'Single map (no report)',     price: 79 },
  ];

  useEffect(() => {
    if (!open || jobs.length > 0) return;
    api.listJobs().then(d => setJobs(d.jobs || [])).catch(() => {});
  }, [open]);

  const buy = async (addon) => {
    if (!jobId && addon !== 'single_map') { setErr('Pick which job to apply the add-on to first.'); return; }
    setBusy(addon); setErr(''); setMsg('');
    try {
      const r = await api.addonCheckout(jobId || 'new', addon);
      if (r?.already_paid) { setMsg(`Already paid for ${addon} on this job. Re-run to use it.`); return; }
      if (r?.url) {
        window.open(r.url, '_blank', 'noopener');
        setMsg('Stripe Checkout opened in a new tab — complete payment there, then return here and re-run your job.');
      }
    } catch (e) { setErr(e.data?.detail || e.message); }
    finally { setBusy(''); }
  };

  return (
    <div className="mt-5 border-t border-slate-200 pt-4">
      <button
        type="button"
        className="w-full flex items-center justify-between text-left p-3 rounded-lg bg-amber-50 border border-amber-300 hover:bg-amber-100 transition"
        onClick={() => setOpen(o => !o)}
      >
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-900 font-bold">Add-on menu</div>
          <div className="text-sm text-amber-900">Buy a one-off DOCX, shapefile, AGOL publish, or single map — $19–$99 each, applies to any tier.</div>
        </div>
        <div className="text-amber-900 text-xl">{open ? '▴' : '▾'}</div>
      </button>

      {open && (
        <div className="mt-3 bg-white border border-slate-200 rounded-lg p-4 space-y-3">
          <div>
            <label className="label text-xs">Apply to which job?</label>
            <select className="input" value={jobId} onChange={e => setJobId(e.target.value)}>
              <option value="">— pick a completed job —</option>
              {jobs.filter(j => j.status === 'completed' || j.status === 'failed').map(j => (
                <option key={j.id} value={j.id}>
                  {(j.client_name || 'Untitled')} · {j.id.slice(0, 6)} · {(j.created_at || '').slice(0, 10)}
                </option>
              ))}
            </select>
            <div className="text-xs text-ink-muted mt-1">Single Map ($29) can be purchased without selecting a job — you'll upload after paying.</div>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
            {ADDONS.map(a => (
              <button
                key={a.key}
                type="button"
                disabled={busy === a.key}
                onClick={() => buy(a.key)}
                className="text-left p-3 rounded-lg border border-slate-200 hover:border-navy hover:shadow-soft transition disabled:opacity-50"
              >
                <div className="text-xs uppercase tracking-widest text-navy font-bold">{a.label}</div>
                <div className="text-lg font-bold text-navy">${a.price}</div>
                <div className="text-xs text-emerald-700 mt-1">
                  {busy === a.key ? 'Opening Stripe…' : 'Buy →'}
                </div>
              </button>
            ))}
          </div>

          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
          {msg && <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">{msg}</div>}
        </div>
      )}
    </div>
  );
}
